import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Building2, Plus, Search, Shield, UserRound } from 'lucide-react';
import QuotaAssignDrawer from '../drawers/QuotaAssignDrawer';
import QuotaScopeDrawer from '../drawers/QuotaScopeDrawer';
import QuotaUserDrawer from '../drawers/QuotaUserDrawer';
import EmptyState from '../components/EmptyState';
import { TablePager, fieldClass, ghostBtn, initials, pageSlice, primaryBtn, selectField, tableHeadClass, tdClass, thClass, trClass } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { QuotaAssignmentStatus, QuotaListingTab, QuotaScope, QuotaUser, QuotaUserStatus } from '../governedMcpTypes';
import {
  buildEffectiveQuotaUsers,
  clientLabel,
  currentQuotaMonth,
  departmentMemberIds,
  filterQuotaUsers,
  findQuotaAssignment,
  formatQuotaMonth,
  formatTokens,
  remainingTokens,
  quotaStatus,
  sourceLabel,
  utilizationPercent,
} from '../quotaUsage';

type UtilFilter = 'all' | QuotaUserStatus;

const LISTING_TABS: { id: QuotaListingTab; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { id: 'department', label: 'Department', icon: Building2 },
  { id: 'role', label: 'Role', icon: Shield },
  { id: 'user', label: 'User', icon: UserRound },
];

function statusLabel(status: QuotaUserStatus): string {
  if (status === 'exceeded') return 'Over limit';
  if (status === 'warning') return 'High';
  return 'On track';
}

function barTone(status: QuotaUserStatus, isLight: boolean): string {
  if (status === 'exceeded') return isLight ? 'bg-rose-500' : 'bg-rose-400';
  if (status === 'warning') return isLight ? 'bg-amber-500' : 'bg-amber-400';
  return isLight ? 'bg-indigo-500' : 'bg-indigo-400';
}

function pillTone(status: QuotaUserStatus, isLight: boolean): string {
  if (status === 'exceeded') return isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/15 text-rose-300';
  if (status === 'warning') return isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function assignmentStatusTone(status: QuotaAssignmentStatus, isLight: boolean): string {
  if (status === 'inactive') return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function sourceTone(source: string, isLight: boolean): string {
  if (source === 'User') return isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300';
  if (source === 'Role') return isLight ? 'bg-sky-50 text-sky-700' : 'bg-sky-500/15 text-sky-300';
  if (source === 'Department') return isLight ? 'bg-violet-50 text-violet-700' : 'bg-violet-500/15 text-violet-300';
  return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
}

type ScopeRow = {
  scope: QuotaScope;
  scopeId: string;
  name: string;
  clientId: string;
  assignedLimit: number | null;
  status: QuotaAssignmentStatus | null;
  users: number;
  used: number;
  remaining: number;
  tokenLimit: number;
};

export default function QuotasSpendingsTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, quotaDirectory } = useGovernedMcp();
  const [listing, setListing] = useState<QuotaListingTab>('department');
  const [query, setQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(currentQuotaMonth());
  const [utilFilter, setUtilFilter] = useState<UtilFilter>('all');
  const [page, setPage] = useState(1);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<QuotaUser | null>(null);
  const [selectedScope, setSelectedScope] = useState<{ scope: QuotaScope; scopeId: string; name: string; clientId: string } | null>(
    null
  );

  const clients = state.clients;
  useEffect(() => {
    if (!clients.length) return;
    if (!clientFilter || !clients.some((client) => client.id === clientFilter)) {
      setClientFilter(clients[0].id);
    }
  }, [clientFilter, clients]);
  const monthUsers = useMemo(
    () =>
      buildEffectiveQuotaUsers(state.apiKeys, state.quotaAssignments, state.quotaUsages, quotaDirectory, monthFilter),
    [monthFilter, quotaDirectory, state.apiKeys, state.quotaAssignments, state.quotaUsages]
  );
  const users = useMemo(
    () => filterQuotaUsers(monthUsers, clientFilter).filter((user) => user.tokenLimit > 0),
    [clientFilter, monthUsers]
  );

  const scopeRows = useMemo(() => {
    const rows: ScopeRow[] = [];
    const items =
      listing === 'department'
        ? quotaDirectory.departments.map((item) => ({ scope: 'department' as const, id: item.id, name: item.name }))
        : quotaDirectory.roles.map((item) => ({ scope: 'role' as const, id: item.id, name: item.name }));
    for (const item of items) {
      const memberIds = new Set(
        listing === 'department'
          ? departmentMemberIds(quotaDirectory, item.id)
          : quotaDirectory.users.filter((user) => user.roleId === item.id).map((user) => user.id)
      );
      if (!clientFilter) continue;
      const clientId = clientFilter;
      const assignment = findQuotaAssignment(state.quotaAssignments, item.scope, item.id, clientId, monthFilter);
      const members = monthUsers.filter((user) => user.userId && memberIds.has(user.userId) && user.clientId === clientId);
      const used = members.reduce((sum, user) => sum + user.tokensUsed, 0);
      const perUserLimit = assignment?.status === 'active' ? assignment.tokenLimit : 0;
      const tokenLimit = perUserLimit * Math.max(memberIds.size, 1);
      rows.push({
        scope: item.scope,
        scopeId: item.id,
        name: item.name,
        clientId,
        assignedLimit: assignment ? assignment.tokenLimit : null,
        status: assignment?.status ?? null,
        users: memberIds.size,
        used,
        remaining: Math.max(0, tokenLimit - used),
        tokenLimit,
      });
    }
    return rows.filter((row) => row.assignedLimit != null && row.assignedLimit > 0);
  }, [clientFilter, listing, monthFilter, monthUsers, quotaDirectory, state.quotaAssignments]);

  const totals = useMemo(() => {
    if (listing === 'user') {
      return users.reduce(
        (acc, user) => {
          acc.limit += user.tokenLimit;
          acc.used += user.tokensUsed;
          acc.remaining += remainingTokens(user);
          return acc;
        },
        { limit: 0, used: 0, remaining: 0 }
      );
    }
    return scopeRows.reduce(
      (acc, row) => {
        acc.limit += row.tokenLimit;
        acc.used += row.used;
        acc.remaining += row.remaining;
        return acc;
      },
      { limit: 0, used: 0, remaining: 0 }
    );
  }, [listing, scopeRows, users]);

  const visibleScopeRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scopeRows.filter((row) => {
      const status = quotaStatus({ tokenLimit: row.tokenLimit, tokensUsed: row.used });
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${row.name} ${clientLabel(row.clientId, clients)}`.toLowerCase().includes(q);
    });
  }, [clients, query, scopeRows, utilFilter]);

  const visibleUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((user) => {
      const status = quotaStatus(user);
      if (utilFilter !== 'all' && status !== utilFilter) return false;
      if (!q) return true;
      return `${user.name} ${user.email} ${user.role} ${clientLabel(user.clientId, clients)}`.toLowerCase().includes(q);
    });
  }, [clients, query, users, utilFilter]);

  useEffect(() => {
    setPage(1);
  }, [listing, query, clientFilter, monthFilter, utilFilter]);

  const listingRows = listing === 'user' ? visibleUsers : visibleScopeRows;
  const pagedUsers = pageSlice(visibleUsers, page);
  const pagedScopeRows = pageSlice(visibleScopeRows, page);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div
          className={`inline-flex rounded-full p-1 ${isLight ? 'bg-white ring-1 ring-black/[0.04]' : 'bg-neutral-900 ring-1 ring-white/10'}`}
        >
          {LISTING_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = listing === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setListing(tab.id);
                  setPage(1);
                  setSelectedScope(null);
                  setSelectedUser(null);
                }}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? isLight
                      ? 'bg-neutral-900 text-white'
                      : 'bg-white text-neutral-900'
                    : isLight
                      ? 'text-neutral-500 hover:text-neutral-800'
                      : 'text-neutral-400 hover:text-white'
                }`}
              >
                <Icon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>
        <button type="button" className={`${primaryBtn()} rounded-full`} onClick={() => setAssignOpen(true)}>
          <Plus size={14} /> Assign quota
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: `Assigned limit · ${formatQuotaMonth(monthFilter)}`, value: formatTokens(totals.limit) },
          { label: 'Current spend', value: formatTokens(totals.used) },
          { label: 'Remaining', value: formatTokens(totals.remaining) },
        ].map((item) => (
          <div
            key={item.label}
            className={`rounded-xl border px-4 py-3 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}
          >
            <p className={`text-[11px] uppercase tracking-wide ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.label}</p>
            <p className={`mt-1 text-[15px] font-semibold tabular-nums ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input
            className={`${fieldClass(isLight)} pl-9`}
            placeholder={listing === 'user' ? 'Search assigned users…' : `Search ${listing}s…`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <input
          type="month"
          className={selectField(isLight)}
          value={monthFilter}
          onChange={(event) => {
            logGovernedMcp('app', 'quota_month_filter', { month: event.target.value });
            setMonthFilter(event.target.value);
          }}
        />
        <select
          className={selectField(isLight)}
          value={clientFilter}
          onChange={(event) => {
            logGovernedMcp('app', 'quota_client_filter', { clientId: event.target.value });
            setClientFilter(event.target.value);
          }}
        >
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </select>
        <select className={selectField(isLight)} value={utilFilter} onChange={(event) => setUtilFilter(event.target.value as UtilFilter)}>
          <option value="all">All utilization</option>
          <option value="ok">On track</option>
          <option value="warning">High</option>
          <option value="exceeded">Over limit</option>
        </select>
      </div>

      {listing !== 'user' && visibleScopeRows.length === 0 ? (
        <EmptyState isLight={isLight} title="No assignments" description="No departments or roles have a quota greater than 0 for this client and month." />
      ) : listing === 'user' && visibleUsers.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title="No assigned users"
          description="No API-key users have a quota greater than 0 for this client and month."
        />
      ) : (
        <div
          className={`overflow-auto min-h-[280px] h-[calc(100vh-22rem)] rounded-2xl border shadow-sm ${
            isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'
          }`}
        >
          <table className="w-full min-w-[960px] text-sm">
            <thead className={tableHeadClass(isLight)}>
              {listing === 'user' ? (
                <tr>
                  <th className={thClass()}> </th>
                  <th className={thClass()}>User</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Effective</th>
                  <th className={thClass()}>Source</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              ) : (
                <tr>
                  <th className={thClass()}> </th>
                  <th className={thClass()}>{listing === 'department' ? 'Department' : 'Role'}</th>
                  <th className={thClass()}>Client</th>
                  <th className={thClass()}>Month</th>
                  <th className={thClass()}>Users</th>
                  <th className={thClass()}>Assigned</th>
                  <th className={thClass()}>Status</th>
                  <th className={thClass()}>Current spend</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Utilization</th>
                </tr>
              )}
            </thead>
            <tbody>
              {listing === 'user'
                ? pagedUsers.map((user) => {
                    const assigned = findQuotaAssignment(
                      state.quotaAssignments,
                      'user',
                      user.userId || '',
                      user.clientId,
                      monthFilter
                    );
                    return (
                      <tr key={user.id} className={trClass(isLight)}>
                        <td className={`${tdClass()} text-left whitespace-nowrap`}>
                          <button
                            type="button"
                            className={`${ghostBtn(isLight)} h-8 px-2.5`}
                            onClick={() => {
                              setSelectedScope(null);
                              setSelectedUser(user);
                            }}
                          >
                            <BarChart3 size={13} />
                            Details
                          </button>
                        </td>
                        <td className={tdClass()}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className={`h-9 w-9 rounded-xl text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                                isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                              }`}
                            >
                              {initials(user.name)}
                            </span>
                            <div className="min-w-0">
                              <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{user.name}</p>
                              <p className={`text-xs truncate ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                {user.email} · {user.role}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className={tdClass()}>{clientLabel(user.clientId, clients)}</td>
                        <td className={tdClass()}>{formatQuotaMonth(monthFilter)}</td>
                        <td className={`${tdClass()} tabular-nums`}>{assigned ? formatTokens(assigned.tokenLimit) : '—'}</td>
                        <td className={tdClass()}>
                          {assigned ? (
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(assigned.status, isLight)}`}>
                              {assigned.status === 'inactive' ? 'Inactive' : 'Active'}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={`${tdClass()} tabular-nums`}>{formatTokens(user.tokenLimit)}</td>
                        <td className={tdClass()}>
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${sourceTone(sourceLabel(user.source), isLight)}`}>
                            {sourceLabel(user.source)}
                          </span>
                        </td>
                        <td className={`${tdClass()} tabular-nums`}>{formatTokens(user.tokensUsed)}</td>
                        <td className={`${tdClass()} tabular-nums`}>{formatTokens(remainingTokens(user))}</td>
                        <td className={tdClass()}>
                          <Utilization user={user} isLight={isLight} />
                        </td>
                      </tr>
                    );
                  })
                : pagedScopeRows.map((row) => {
                    const rowKey = `${row.scope}-${row.scopeId}-${row.clientId}-${monthFilter}`;
                    return (
                      <tr key={rowKey} className={trClass(isLight)}>
                        <td className={`${tdClass()} text-left whitespace-nowrap`}>
                          <button
                            type="button"
                            className={`${ghostBtn(isLight)} h-8 px-2.5`}
                            onClick={() => {
                              setSelectedUser(null);
                              setSelectedScope({
                                scope: row.scope,
                                scopeId: row.scopeId,
                                name: row.name,
                                clientId: row.clientId || clientFilter,
                              });
                            }}
                          >
                            <BarChart3 size={13} />
                            Details
                          </button>
                        </td>
                        <td className={tdClass()}>
                          <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.name}</p>
                        </td>
                        <td className={tdClass()}>{row.clientId ? clientLabel(row.clientId, clients) : 'Select a client'}</td>
                        <td className={tdClass()}>{formatQuotaMonth(monthFilter)}</td>
                        <td className={`${tdClass()} tabular-nums`}>{row.users}</td>
                        <td className={`${tdClass()} tabular-nums`}>
                          {row.assignedLimit == null ? '—' : formatTokens(row.assignedLimit)}
                        </td>
                        <td className={tdClass()}>
                          {row.status ? (
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(row.status, isLight)}`}>
                              {row.status === 'inactive' ? 'Inactive' : 'Active'}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.used)}</td>
                        <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.remaining)}</td>
                        <td className={tdClass()}>
                          <Utilization user={{ tokenLimit: row.tokenLimit, tokensUsed: row.used }} isLight={isLight} />
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      )}
      {listingRows.length > 0 ? (
        <TablePager
          page={page}
          total={listingRows.length}
          isLight={isLight}
          onPage={setPage}
          noun={listing === 'user' ? 'user' : listing}
        />
      ) : null}

      <QuotaAssignDrawer
        open={assignOpen}
        listing={listing}
        clientId={clientFilter}
        month={monthFilter}
        isLight={isLight}
        onClose={() => setAssignOpen(false)}
        onToast={onToast}
      />
      <QuotaUserDrawer
        user={selectedUser}
        clientFilter={clientFilter}
        month={monthFilter}
        isLight={isLight}
        onClose={() => setSelectedUser(null)}
      />
      <QuotaScopeDrawer
        open={Boolean(selectedScope)}
        scope={selectedScope?.scope || 'department'}
        scopeId={selectedScope?.scopeId || ''}
        name={selectedScope?.name || ''}
        clientId={selectedScope?.clientId || clientFilter}
        month={monthFilter}
        isLight={isLight}
        onClose={() => setSelectedScope(null)}
        onOpenUser={(user) => {
          setSelectedScope(null);
          setSelectedUser(user);
        }}
      />
    </div>
  );
}

function Utilization({
  user,
  isLight,
}: {
  user: Pick<QuotaUser, 'tokenLimit' | 'tokensUsed'>;
  isLight: boolean;
}) {
  const status = quotaStatus(user);
  const percent = utilizationPercent(user);
  return (
    <div className="min-w-[140px]">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${pillTone(status, isLight)}`}>{statusLabel(status)}</span>
        <span className={`text-[11px] tabular-nums ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{percent}%</span>
      </div>
      <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
        <div className={`h-full rounded-full ${barTone(status, isLight)}`} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
    </div>
  );
}
